import { FC, useMemo } from 'react';
import illustration from '@svg/illustrations/send_money_success.svg';
import illustrationBankSend from '@svg/illustrations/send_money_bank.svg';
import illustrationRequest from '@svg/illustrations/success_request.svg';
import illustrationRequestSchedule from '@svg/illustrations/request_shcedule.svg';
import illustrationLoad from '@svg/illustrations/load_balance.svg';
import illustrationLoadSchedule from '@svg/illustrations/load_balance_schedule.svg';
import illustrationSchedule from '@svg/illustrations/send_schedule_success.svg';
import illustrationBill from '@svg/illustrations/pay_bill.svg';
import illustrationBillSchedule from '@svg/illustrations/pay_bill_schedule.svg';
import giftCardCreated from '@svg/illustrations/gift_card_created.svg';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { EFundsOperation } from '@enum';
import { useUserContext } from '@hooks/context/useUserContext';

interface IProps {
    isSchedule: boolean;
    onReset: () => void;
    method: EFundsOperation;
    isExternal?: boolean;
    isBulkGiftCards?: boolean;
}

const Success: FC<IProps> = ({ isSchedule, onReset, method, isExternal, isBulkGiftCards }) => {
    const { submitLocale } = useLocales();
    const navigate = useNavigate();
    const { loyalty } = useUserContext();

    const data = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    illustration: illustration,
                    illustrationSchedule: illustrationSchedule,
                    description:
                        'Payment sent to processing! We will notify you, when the payment will be processed',
                    scheduleDescription:
                        'Your payment has been successfully scheduled. You can check the progress of your scheduled payments and manage them on',
                    externalDescription: `Your payment has been sent. The recipient will be able to access the money after setting up their wallet and verifying their identity. You’ll earn <span>${loyalty?.invite_sender_points || 0} points</span> once they’re verified.`,
                    submitButton: 'New payment',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return {
                    illustration: illustration,
                    illustrationSchedule: '',
                    description:
                        'Your payment is being processed. We’ll notify you once it’s complete.',
                    scheduleDescription: '',
                    externalDescription: '',
                    submitButton: 'New payment',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return {
                    illustration: illustrationBankSend,
                    illustrationSchedule: illustrationBankSend,
                    description:
                        'Your withdrawal is being processed. It may take up to 3 business days. We’ll notify you as soon as the money is deposited into your bank account.',
                    scheduleDescription:
                        'Your withdrawal has been successfully scheduled. It may take up to 3 business days. We’ll notify you as soon as the money is deposited into your bank account.',
                    externalDescription: '',
                    submitButton: 'New withdrawal',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return {
                    illustration: illustrationRequest,
                    illustrationSchedule: illustrationRequestSchedule,
                    description:
                        'Your payment request has been sent. We’ll notify you when the recipient responds. If the request is accepted, the money will be deposited into your wallet.',
                    scheduleDescription:
                        'Your payment request has been successfully scheduled. You’ll receive a notification once the recipient accepts or declines your request. You can check the progress of your scheduled requests and manage them on',
                    externalDescription: '',
                    submitButton: 'New payment request',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return {
                    illustration: illustrationRequest,
                    illustrationSchedule: illustrationRequestSchedule,
                    description:
                        'Your payment request has been sent. We’ll notify you when the recipient responds. If the request is accepted, the money will be deposited into your wallet.',
                    scheduleDescription: '',
                    externalDescription: '',
                    submitButton: 'New payment request',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return {
                    illustration: illustrationLoad,
                    illustrationSchedule: illustrationLoadSchedule,
                    description:
                        'Your balance load request has been sent. The deposit may take up to 3 business days. We’ll notify you as soon as the money is deposited into your wallet.',
                    scheduleDescription:
                        'Your balance load request has been scheduled. The deposit may take up to 3 business days. We’ll notify you as soon as the money is deposited into your wallet.',
                    externalDescription: '',
                    submitButton: 'New load balance',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return {
                    illustration: illustrationLoad,
                    illustrationSchedule: illustrationLoadSchedule,
                    description:
                        'Your balance load request has been sent. We’ll notify you as soon as the money is deposited into your wallet.',
                    scheduleDescription: '',
                    externalDescription: '',
                    submitButton: 'New load balance',
                    scheduleLink: routes.send_and_request_schedule + `?type=${method}`
                };
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return {
                    illustration: illustrationBill,
                    illustrationSchedule: illustrationBillSchedule,
                    description:
                        'Your bill payment is being processed. It may take some time for the payment to appear on your biller’s account.',
                    scheduleDescription:
                        'Your bill payment has been scheduled. It may take some time for the payment to appear on your biller’s account.',
                    externalDescription: '',
                    submitButton: 'New bill payment',
                    scheduleLink: routes.pay_bills_schedule,
                    linkText: 'Scheduled bill payments page'
                };
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return {
                    illustration: giftCardCreated,
                    illustrationSchedule: giftCardCreated,
                    description: isBulkGiftCards
                        ? 'Your gift cards have been created successfully'
                        : 'Your gift card has been created successfully',
                    scheduleDescription: '',
                    externalDescription: '',
                    submitButton: isBulkGiftCards
                        ? 'Create another gift cards'
                        : 'Create another gift card',
                    // submitButton: isBulkGiftCards ? 'Activate gift cards' : 'Activate gift card',
                    scheduleLink: '',
                    linkText: ''
                };
        }
    }, [method, isSchedule]);

    if (!data) return null;

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <img
                src={isSchedule ? data.illustrationSchedule : data.illustration}
                alt="illustration"
            />
            <div className={styles.text_wrapper}>
                <div className={styles.title}>Success!</div>
                <div className={styles.description}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: isSchedule
                                ? data.scheduleDescription
                                : isExternal
                                  ? data.externalDescription
                                  : data.description
                        }}
                    />{' '}
                    {isSchedule && (
                        <>
                            <span>
                                You can view and manage your scheduled payments on the{' '}
                                <Button
                                    variant="text-gold"
                                    onClick={() => navigate(data.scheduleLink)}>
                                    {data?.linkText || 'Scheduled payments'}
                                </Button>{' '}
                                page.
                            </span>
                        </>
                    )}
                </div>
            </div>
            <div className={styles.actions}>
                <Button variant="outlined-primary" onClick={() => navigate(routes.dashboard)}>
                    {submitLocale('done')}
                </Button>
                <Button variant="primary" onClick={onReset}>
                    {data.submitButton}
                </Button>
            </div>
        </motion.div>
    );
};

export default Success;
